// Sheets for the user's own albums: name one (create / rename) and file a recipe in albums.
import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Check, Folder, Plus } from '../icons';
import { useT } from '../i18n';
import type { AlbumCover, MyAlbum } from '../types';

function useEscape(onClose: () => void) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);
}

/** Create (no initial name) or rename an album. onSaved gets the created album or the new name. */
export function NameSheet({ initial = '', albumId, onClose, onSaved }: {
  initial?: string; albumId?: number; onClose: () => void; onSaved: (a: MyAlbum | string) => void;
}) {
  const t = useT();
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  useEscape(onClose);
  const submit = async (e: Event) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    setErr('');
    try {
      if (albumId) { await api.renameAlbum(albumId, n); onSaved(n); } else onSaved(await api.createAlbum(n));
    } catch (ex) {
      setErr((ex as ApiError).message);
      setBusy(false);
    }
  };
  return (
    <div class="scrim" role="dialog" aria-modal="true" aria-labelledby="name-title" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form class="sheet" onSubmit={submit}>
        <div class="sheet-handle" aria-hidden="true" />
        <h2 id="name-title" class="h2">{albumId ? t('album.rename') : t('albums.new')}</h2>
        <label for="album-name" class="muted" style="font-size:14px">{t('album.nameLabel')}</label>
        <input id="album-name" ref={input} class="input" maxLength={60} autoComplete="off" value={name}
          placeholder={t('album.namePlaceholder')} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        {err && <div class="error-text" role="alert">{err}</div>}
        <button class="btn btn-primary" type="submit" disabled={busy || !name.trim()}>{albumId ? t('common.save') : t('album.create')}</button>
        <button type="button" class="btn-text" onClick={onClose}>{t('common.cancel')}</button>
      </form>
    </div>
  );
}

/** "Add to album" from a recipe page: tick the user's albums, or make a new one. */
export function AddToAlbumSheet({ reelId, inAlbums, onClose, onChange, onToast }: {
  reelId: number; inAlbums: number[]; onClose: () => void; onChange: (ids: number[]) => void; onToast: (m: string) => void;
}) {
  const t = useT();
  const [mine, setMine] = useState<AlbumCover[] | null>(null);
  const [ids, setIds] = useState(inAlbums);
  const [naming, setNaming] = useState(false);
  useEscape(onClose);
  const load = () => api.albums('craving').then((v) => setMine(v.mine)).catch((e: ApiError) => { setMine([]); onToast(e.message); });
  useEffect(() => { load(); }, []);

  const toggle = async (c: AlbumCover) => {
    const id = Number(c.key);
    const on = !ids.includes(id);
    const next = on ? [...ids, id] : ids.filter((x) => x !== id);
    setIds(next);
    try {
      if (on) await api.addToAlbum(id, reelId); else await api.removeFromAlbum(id, reelId);
      onChange(next);
      onToast(t(on ? 'albumSheet.added' : 'albumSheet.removed', { name: c.name ?? '' }));
    } catch (e) {
      setIds(ids);
      onToast((e as ApiError).message);
    }
  };

  if (naming) {
    return <NameSheet onClose={() => setNaming(false)} onSaved={async (a) => {
      setNaming(false);
      const album = a as MyAlbum;
      try {
        await api.addToAlbum(album.id, reelId);
        const next = [...ids, album.id];
        setIds(next);
        onChange(next);
        onToast(t('albumSheet.added', { name: album.name }));
      } catch (e) { onToast((e as ApiError).message); }
      load();
    }} />;
  }

  return (
    <div class="scrim" role="dialog" aria-modal="true" aria-labelledby="album-sheet-title" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="sheet">
        <div class="sheet-handle" aria-hidden="true" />
        <h2 id="album-sheet-title" class="h2">{t('albumSheet.title')}</h2>
        {mine === null && <div class="skeleton" style="height:60px" aria-label={t('common.loading')} />}
        {mine?.length === 0 && <div class="muted">{t('albumSheet.none')}</div>}
        {mine && mine.length > 0 && (
          <div class="rows">
            {mine.map((c) => {
              const on = ids.includes(Number(c.key));
              return (
                <button class="row" aria-pressed={on} onClick={() => toggle(c)}>
                  <Folder />
                  <span class="grow"><span style="font-weight:700">{c.name}</span><span class="sub">{t('n.recipes', { n: c.count + (on && !inAlbums.includes(Number(c.key)) ? 1 : 0) - (!on && inAlbums.includes(Number(c.key)) ? 1 : 0) })}</span></span>
                  <span class="checkbox" style={on ? 'background:var(--ink);border-color:var(--ink)' : ''}>{on && <Check style="color:var(--ground)" />}</span>
                </button>
              );
            })}
          </div>
        )}
        <button class="btn btn-outline" onClick={() => setNaming(true)}><Plus size={20} />{t('albums.new')}</button>
        <button class="btn btn-primary" onClick={onClose}>{t('common.done')}</button>
      </div>
    </div>
  );
}
